import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import style from './flag-address-modal.module.less';
import { messages } from './messages';
import ModalPortal from '../../../components/modal-portal';
import { GuidService } from 'ui-components/services/guid-service';

type FlagAddressModalProps = {
  onClose: () => void;
  onFlag: (reason: string) => void;
  isFlagging?: boolean;
};

const MAX_REASON_LENGTH = 500;

export const FlagAddressModal = ({ onClose, onFlag, isFlagging = false }: FlagAddressModalProps) => {
  const intl = useIntl();
  const modalHeaderId = useMemo(() => GuidService.newGuid(), []);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleReasonChange = useCallback(
    (value: string) => {
      setReason(value);

      if (value.length > MAX_REASON_LENGTH) {
        setError(intl.formatMessage(messages.reasonMaxLengthError));
      } else {
        setError(null);
      }
    },
    [intl],
  );

  const handleFlag = useCallback(() => {
    const trimmedReason = reason.trim();

    if (!trimmedReason) {
      setError(intl.formatMessage(messages.reasonRequiredError));
      return;
    }

    if (trimmedReason.length > MAX_REASON_LENGTH) {
      return; // Error already shown from onChange
    }

    onFlag(trimmedReason);
  }, [reason, onFlag, intl]);

  const hasError = Boolean(error);

  return (
    <ModalPortal onClose={onClose}>
      <Modal modalHeaderId={modalHeaderId} size='small'>
        <ModalHeader id={modalHeaderId}>
          <Text textType={TextType.H3} value={messages.header} />
        </ModalHeader>
        <ModalBody>
          <FlexColumn className={style['body-content']} gap={Gap.Space12}>
            <Text textType={TextType.Description} value={messages.description} />
            <div className={style['textarea-container']}>
              <FloatTextArea
                disabled={isFlagging}
                hasError={hasError}
                label={intl.formatMessage(messages.reasonLabel)}
                maxLength={MAX_REASON_LENGTH}
                onChange={handleReasonChange}
                value={reason}
              />
              {hasError && error && (
                <InlineMessage className={style['error-message']} type={INLINE_MESSAGE_TYPES.error}>
                  {error}
                </InlineMessage>
              )}
            </div>
          </FlexColumn>
        </ModalBody>
        <ModalFooter>
          <ModalFooterCancelButton disabled={isFlagging} />
          <Button disabled={isFlagging || hasError} onClick={handleFlag} theme={THEME_CONSTANTS.danger}>
            <Text textType={TextType.Label} value={isFlagging ? messages.flagging : messages.flagAddress} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
